// ============================================================================
// AdminEmailBroadcast - branded mass email from the admin panel
// ============================================================================
// Composes a PokerSweepStars-branded update email and sends it through the
// Cloud Functions SMTP transport (the same one used for 2FA recovery).
//
// Two guards before anything reaches a real inbox:
//   1. An SMTP status check, run on mount, that verifies the connection
//      instead of assuming a config value is present.
//   2. "Send test to me", which delivers only to the signed-in admin so the
//      rendering can be checked in a real client before the list goes out.
//
// Recipients with emailOptOut are skipped server-side; every message carries
// a one-click unsubscribe link.
// ============================================================================
import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Input, Textarea, HStack, VStack, Button, Alert, AlertIcon,
  FormControl, FormLabel, FormHelperText, Badge, Spinner, Divider, Select,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { Mail, Send, RefreshCw, Eye, AlertTriangle, Users } from 'lucide-react';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';

interface Draft {
  subject: string;
  preheader: string;
  heading: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
}

const APP_URL = 'https://pokersweepstars.web.app';

/**
 * Starting points, not templates to send verbatim - each one still gets
 * edited before it goes out. They exist so a routine announcement does not
 * start from a blank box.
 */
const PRESETS: Record<string, Draft & { name: string }> = {
  blank: {
    name: 'Blank',
    subject: '', preheader: '', heading: '', body: '', ctaLabel: '', ctaUrl: '',
  },
  packDrop: {
    name: 'Pack Drop launch',
    subject: 'Your first Pack Drop is waiting',
    preheader: 'Play one hand to unlock it. Real PSS Coins up to $50 inside.',
    heading: 'Pack Drop is live',
    body: `Twice a week, a free pack is yours.

Play a single hand to unlock it, then pick a crate and watch the reel decide. Prizes run from 1,000 PlayCoins all the way to 100,000 - and some packs hold real PSS Coins, up to $50.

This cycle's pack is open now. A new one drops every few days - unopened packs do not carry over.`,
    ctaLabel: 'Open my pack',
    ctaUrl: `${APP_URL}/?packDrop=1`,
  },
  dailySpin: {
    name: 'Daily Spin reminder',
    subject: "You haven't spun today",
    preheader: 'One free spin a day. Real PSS Coins are on the wheel.',
    heading: 'Your daily spin is ready',
    body: `The wheel resets every day at midnight, and today's spin is still sitting there.

Most segments pay PlayCoins - but PSS Coins are on there too, and they carry a 1x playthrough.

It takes five seconds.`,
    ctaLabel: 'Spin the wheel',
    ctaUrl: APP_URL,
  },
  tournament: {
    name: 'Tournament announcement',
    subject: 'New tournament on the schedule',
    preheader: 'Seats are open now.',
    heading: 'See you at the tables',
    body: `A new event just went up on the tournament schedule.

Registration is open, late reg runs through the first few levels, and re-entry is available until the money bubble.

Seats fill fastest in the hour before the start.`,
    ctaLabel: 'View the schedule',
    ctaUrl: `${APP_URL}/tournaments`,
  },
  whatsNew: {
    name: "What's New digest",
    subject: "What's new at PokerSweepStars",
    preheader: 'Home games, groups, voice and video, Run It Multiple Times - all live.',
    heading: "Here's what's new",
    body: `A lot has landed since your last visit.

Home Games: private, rake-free poker nights. Friends join free with an invite code, the host runs the room, and every game saves a results ledger.

Groups: find your crew or build one. Rally the whole group to a live table with one tap, or schedule a game and we remind everyone who registers.

Voice and video at the table: faces on, live, on every platform - iPhone included.

Run It Multiple Times: all-in with cards to come? Run the board once, twice or three times.

Plus automatic bomb pots, straddles, seven-deuce bounties, and a smoother table everywhere. The full changelog is on our site.`,
    ctaLabel: "See what's new",
    ctaUrl: APP_URL,
  },
  getInAction: {
    name: 'Get in the action',
    subject: 'The tables are running - come get in the action',
    preheader: 'Quick Match seats you in seconds. Your daily bonuses are waiting.',
    heading: 'Your seat is open',
    body: `The felt is live right now, and getting in takes one tap.

Quick Match drops you at a table in seconds. Your daily spin and login bonus are waiting, and if you have not opened this cycle's Pack Drop, play one hand to unlock it.

Want a game with your people instead? Join a group, rally them to a table, or host a rake-free home game tonight.

See you at the tables.`,
    ctaLabel: 'Take my seat',
    ctaUrl: `${APP_URL}/tables`,
  },
  emergency: {
    name: 'EMERGENCY: service notice',
    subject: 'PokerSweepStars service notice',
    preheader: 'Important information about your account and our service.',
    heading: 'Service notice',
    body: `EDIT BEFORE SENDING - this is a skeleton for urgent notices.

What happened: [one plain sentence - e.g. "We are performing emergency maintenance" / "Tables are temporarily paused."]

What it means for you: [chips, balances and game history are safe - say so explicitly if true.]

What happens next: [when service resumes, or where updates will be posted.]

We will keep this short and follow up the moment there is more to share. Thank you for bearing with us.`,
    ctaLabel: 'Check status',
    ctaUrl: APP_URL,
  },
  bigUpdate: {
    name: 'Launch: the big update',
    subject: 'PokerSweepStars just got a lot bigger',
    preheader: 'Home games, groups, voice and video at the table - all live now.',
    heading: 'The update is live',
    body: `We have been building all month, and it is all live today.

Home Games: host a private, rake-free poker night in two taps. Friends join free with an invite code, you run the room, and the results sheet writes itself.

Groups: build your poker crew. Rally everyone to a live table with one tap, or schedule a game and we remind everyone who registers.

Voice and video at the table: faces on, trash talk flowing - on your phone, in the app.

Plus Run It Multiple Times, automatic bomb pots, and a smoother table everywhere.`,
    ctaLabel: 'See what is new',
    ctaUrl: APP_URL,
  },
  homeGames: {
    name: 'Home Games invite',
    subject: 'Bring your home game to PSS',
    preheader: 'Private, rake-free, free for your friends to join.',
    heading: 'Poker night, minus the setup',
    body: `Your regular game deserves better than a group-chat scheduling war.

Host a private home game in two taps: friends join free with an invite code (no wallet needed), you manage the chips as host, and every game saves a results sheet everyone can see and share.

Rake-free. Voice and video at the table. Works on any phone or browser.`,
    ctaLabel: 'Host a home game',
    ctaUrl: `${APP_URL}/tables`,
  },
  voiceVideo: {
    name: 'Voice & video launch',
    subject: 'Your poker face, live at the table',
    preheader: 'Voice and video chat just landed in the app.',
    heading: 'Faces on. Talk your game.',
    body: `Poker without table talk is just a card dealer.

Voice and video chat is now live at the table on every platform - iPhone app included. Join the mic between hands, flip your camera on, and needle your friends like you are all in the same room.

It is the thing no other poker app has. Come hear the difference.`,
    ctaLabel: 'Sit down and say hi',
    ctaUrl: `${APP_URL}/tables`,
  },
};

const AUDIENCES: Array<{ key: string; label: string; blurb: string }> = [
  { key: 'all', label: 'Everyone with an email', blurb: 'Every account with an address on file' },
  { key: 'active7', label: 'Active last 7 days', blurb: 'Signed in or played this week' },
  { key: 'inactive14', label: 'Inactive 14+ days', blurb: 'Win-back: quiet for two weeks or more' },
  { key: 'new30', label: 'Joined last 30 days', blurb: 'New signups still finding their feet' },
];

const AdminEmailBroadcast: React.FC = () => {
  const [draft, setDraft] = useState<Draft>(PRESETS.blank);
  const [preset, setPreset] = useState('blank');
  const [status, setStatus] = useState<{
    configured?: boolean; verified?: boolean; from?: string; detail?: string; error?: string;
    provider?: 'sendgrid' | 'gmail' | 'none';
  } | null>(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState<'test' | 'all' | null>(null);
  const [report, setReport] = useState<{ ok: boolean; msg: string } | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const preview = useDisclosure();

  // Audience targeting + campaign history
  const [audience, setAudience] = useState('all');
  const [audienceCounts, setAudienceCounts] = useState<Record<string, number> | null>(null);
  const [history, setHistory] = useState<any[] | null>(null);

  // Per-campaign recipients: who got it, and how many a resend would reach
  const recipientsModal = useDisclosure();
  const [recipients, setRecipients] = useState<{
    subject: string; loading: boolean; error?: string;
    counts?: Record<string, number>;
    list?: Array<{ email: string; status: string; error: string | null }>;
  } | null>(null);
  const [recipientFilter, setRecipientFilter] = useState<'all' | 'sent' | 'failed' | 'bounced'>('all');
  const [remaining, setRemaining] = useState<{ subject: string; count: number | null; already: number } | null>(null);

  const openRecipients = async (subject: string) => {
    setRecipientFilter('all');
    setRecipients({ subject, loading: true });
    recipientsModal.onOpen();
    const r = await gameApiService.emailCampaignRecipients(subject);
    setRecipients(r.success
      ? { subject, loading: false, counts: r.counts, list: r.recipients }
      : { subject, loading: false, error: r.error || 'Could not load recipients' });
  };

  // Before sending: how many already have this subject (they are skipped)
  const openConfirm = async () => {
    onOpen();
    const subject = draft.subject.trim();
    setRemaining({ subject, count: null, already: 0 });
    const r = await gameApiService.emailCampaignRecipients(subject, audience);
    if (r.success) {
      setRemaining({ subject, count: r.remaining ?? null,
        already: (r.counts?.sent || 0) + (r.counts?.bounced || 0) });
    }
  };

  useEffect(() => {
    gameApiService.emailAudience().then(r => {
      if (r.success && r.counts) setAudienceCounts(r.counts as any);
    }).catch(() => {});
    gameApiService.emailCampaigns().then(r => {
      if (r.success) setHistory(r.campaigns || []);
    }).catch(() => {});
  }, []);

  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft(d => ({ ...d, [k]: e.target.value }));

  const checkStatus = async () => {
    setChecking(true);
    const r = await gameApiService.emailStatus();
    setChecking(false);
    setStatus(r.success ? r : { error: r.error });
  };

  useEffect(() => { checkStatus(); }, []);

  const applyPreset = (key: string) => {
    setPreset(key);
    const p = PRESETS[key];
    if (p) setDraft({ subject: p.subject, preheader: p.preheader, heading: p.heading,
      body: p.body, ctaLabel: p.ctaLabel, ctaUrl: p.ctaUrl });
  };

  const ready = !!(draft.subject.trim() && draft.heading.trim() && draft.body.trim());

  const send = async (testOnly: boolean) => {
    setSending(testOnly ? 'test' : 'all');
    setReport(null);
    const r = await gameApiService.emailBroadcast({
      subject: draft.subject.trim(),
      heading: draft.heading.trim(),
      body: draft.body,
      preheader: draft.preheader.trim() || undefined,
      ctaLabel: draft.ctaLabel.trim() || undefined,
      ctaUrl: draft.ctaUrl.trim() || undefined,
      audience,
      testOnly,
    });
    setSending(null);
    onClose();
    if (!r.success) { setReport({ ok: false, msg: r.error || 'Send failed' }); return; }
    setReport({
      ok: true,
      msg: testOnly
        ? `Test sent to ${r.to || 'your address'}. Check the inbox - and the spam folder.`
        : `Sent to ${r.sentTo ?? 0} addresses in "${AUDIENCES.find(a => a.key === audience)?.label || audience}"` +
          `${r.skipped ? `, ${r.skipped} already had it and were skipped` : ''}` +
          `${r.failed ? `, ${r.failed} failed` : ''}` +
          `${r.notAttempted ? `, ${r.notAttempted} not sent yet (${r.stopReason === 'quota' ? 'daily sending limit reached' : 'run paused'})` : ''}.` +
          `${(r.failed || r.notAttempted) ? ' Press Send campaign again with the same subject to reach just the ones who missed it.' : ''}`,
    });
    if (!testOnly) {
      gameApiService.emailCampaigns().then(h => { if (h.success) setHistory(h.campaigns || []); }).catch(() => {});
    }
  };

  // Approximation of the server template - close enough to catch a bad line
  // break or a heading that runs long, without duplicating the real HTML.
  const previewBody = draft.body.split(/\n\s*\n/).filter(Boolean);

  return (
    <VStack spacing={5} align="stretch">
      <Box>
        <HStack mb={1}>
          <Mail size={18} color={POKER_COLORS.cyan} />
          <Heading size="md" color={POKER_COLORS.cyan}>Email Broadcast</Heading>
        </HStack>
        <Text color="gray.400" fontSize="sm">
          Sends a branded update to every account with an email address on file,
          skipping anyone who has unsubscribed.
        </Text>
      </Box>

      {/* SMTP status */}
      <Box bg="gray.800" borderRadius="lg" p={3} borderWidth="1px"
           borderColor={status?.verified ? 'green.600' : status ? 'orange.600' : 'gray.700'}>
        <HStack justify="space-between" align="start">
          <HStack align="start" spacing={3}>
            {checking ? <Spinner size="sm" color={POKER_COLORS.cyan} /> :
              status?.verified ? (
                <Badge colorScheme={status.provider === 'sendgrid' ? 'blue' : 'green'}>
                  {status.provider === 'sendgrid' ? 'SENDGRID OK' : 'SMTP OK'}
                </Badge>
              ) : <Badge colorScheme="orange">SMTP</Badge>}
            <Box>
              <Text color="white" fontSize="sm" fontWeight="bold">
                {checking ? 'Checking mail transport...' :
                  status?.verified ? `Connected as ${status.from || 'configured sender'}` :
                  status?.configured === false ? 'Not configured' :
                  status?.error ? 'Status check failed' : 'Not verified'}
              </Text>
              <Text color="gray.400" fontSize="xs">{status?.detail || status?.error || ''}</Text>
            </Box>
          </HStack>
          <Button size="xs" variant="ghost" colorScheme="cyan" onClick={checkStatus}
                  leftIcon={<RefreshCw size={13} />} isDisabled={checking}>
            Recheck
          </Button>
        </HStack>
      </Box>

      <Divider borderColor="gray.700" />

      <FormControl>
        <FormLabel color="gray.300" fontSize="sm">Start from</FormLabel>
        <Select value={preset} onChange={e => applyPreset(e.target.value)}
                bg="gray.800" borderColor="gray.600" color="white" size="sm" maxW="280px">
          {Object.entries(PRESETS).map(([k, p]) => (
            <option key={k} value={k} style={{ background: '#1A202C' }}>{p.name}</option>
          ))}
        </Select>
      </FormControl>

      {/* Audience targeting with live counts */}
      <FormControl>
        <FormLabel color="gray.300" fontSize="sm">Audience</FormLabel>
        <Select value={audience} onChange={e => setAudience(e.target.value)}
                bg="gray.800" borderColor="gray.600" color="white" size="sm" maxW="360px">
          {AUDIENCES.map(a => (
            <option key={a.key} value={a.key} style={{ background: '#1A202C' }}>
              {a.label}{audienceCounts ? ` (${audienceCounts[a.key] ?? 0})` : ''}
            </option>
          ))}
        </Select>
        <FormHelperText color="gray.500" fontSize="xs">
          {AUDIENCES.find(a => a.key === audience)?.blurb}
          {audienceCounts && ` - ${audienceCounts[audience] ?? 0} recipients`}
          {audienceCounts && audienceCounts.optedOut > 0 && ` (${audienceCounts.optedOut} unsubscribed are always excluded)`}
        </FormHelperText>
      </FormControl>

      <FormControl isRequired>
        <FormLabel color="gray.300" fontSize="sm">Subject</FormLabel>
        <Input value={draft.subject} onChange={set('subject')} bg="gray.800"
               borderColor="gray.600" color="white" placeholder="Your first Pack Drop is waiting" />
        <FormHelperText color="gray.500" fontSize="xs">
          {draft.subject.length} characters - most clients cut off around 45 on mobile.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel color="gray.300" fontSize="sm">Preview text</FormLabel>
        <Input value={draft.preheader} onChange={set('preheader')} bg="gray.800"
               borderColor="gray.600" color="white"
               placeholder="The grey line shown after the subject in the inbox list" />
      </FormControl>

      <FormControl isRequired>
        <FormLabel color="gray.300" fontSize="sm">Heading</FormLabel>
        <Input value={draft.heading} onChange={set('heading')} bg="gray.800"
               borderColor="gray.600" color="white" placeholder="Pack Drop is live" />
      </FormControl>

      <FormControl isRequired>
        <FormLabel color="gray.300" fontSize="sm">Body</FormLabel>
        <Textarea value={draft.body} onChange={set('body')} bg="gray.800" rows={9}
                  borderColor="gray.600" color="white"
                  placeholder={'Blank lines start a new paragraph.\n\nLike this.'} />
        <FormHelperText color="gray.500" fontSize="xs">
          Plain text. A blank line starts a new paragraph; single line breaks are kept.
        </FormHelperText>
      </FormControl>

      <HStack align="start" spacing={3}>
        <FormControl>
          <FormLabel color="gray.300" fontSize="sm">Button label</FormLabel>
          <Input value={draft.ctaLabel} onChange={set('ctaLabel')} bg="gray.800"
                 borderColor="gray.600" color="white" placeholder="Open my pack" />
        </FormControl>
        <FormControl>
          <FormLabel color="gray.300" fontSize="sm">Button link</FormLabel>
          <Input value={draft.ctaUrl} onChange={set('ctaUrl')} bg="gray.800"
                 borderColor="gray.600" color="white" placeholder={`${APP_URL}/?packDrop=1`} />
        </FormControl>
      </HStack>
      <Text color="gray.500" fontSize="xs" mt={-2}>
        The button only appears when both fields are filled in.
      </Text>

      {report && (
        <Alert status={report.ok ? 'success' : 'error'} borderRadius="md" bg={report.ok ? 'green.900' : 'red.900'}>
          <AlertIcon />
          <Text fontSize="sm" color="white">{report.msg}</Text>
        </Alert>
      )}

      <HStack spacing={3} pt={1}>
        <Button leftIcon={<Eye size={15} />} variant="outline" colorScheme="cyan"
                onClick={preview.onOpen} isDisabled={!ready}>
          Preview
        </Button>
        <Button leftIcon={<Send size={15} />} colorScheme="cyan"
                onClick={() => send(true)} isLoading={sending === 'test'}
                isDisabled={!ready || sending !== null}>
          Send test to me
        </Button>
        <Button leftIcon={<Mail size={15} />} colorScheme="yellow"
                onClick={openConfirm} isDisabled={!ready || sending !== null}>
          Send campaign
        </Button>
      </HStack>

      {/* Campaign history */}
      {history && history.length > 0 && (
        <Box>
          <Divider borderColor="gray.700" mb={3} />
          <Text color="gray.300" fontSize="sm" fontWeight="bold" mb={2}>Recent campaigns</Text>
          <VStack align="stretch" spacing={1}>
            {history.slice(0, 8).map((c: any) => (
              <HStack key={c.id} justify="space-between" bg="gray.800" px={3} py={2} borderRadius="md">
                <Box minW={0}>
                  <Text color="white" fontSize="xs" noOfLines={1}>{c.subject}</Text>
                  <Text color="gray.500" fontSize="10px">
                    {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''} - {AUDIENCES.find(a => a.key === c.audience)?.label || c.audience}
                  </Text>
                </Box>
                <HStack flexShrink={0} spacing={2}>
                  <Text color="gray.400" fontSize="xs">
                    {c.sentTo}/{c.candidates} sent{c.failed ? `, ${c.failed} failed` : ''}
                    {c.skipped ? `, ${c.skipped} skipped` : ''}
                  </Text>
                  <Button size="xs" variant="outline" colorScheme="cyan" leftIcon={<Users size={12} />}
                          onClick={() => openRecipients(c.subject)}>
                    Recipients
                  </Button>
                </HStack>
              </HStack>
            ))}
          </VStack>
          <Text color="gray.500" fontSize="xs" mt={2}>
            Sending again with the same subject only goes to people who have not received it.
          </Text>
        </Box>
      )}

      {/* Recipients of one campaign */}
      <Modal isOpen={recipientsModal.isOpen} onClose={recipientsModal.onClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkTeal} borderColor="#1B4055" borderWidth="1px">
          <ModalHeader color={POKER_COLORS.cyan} fontSize="md">
            Recipients
            <Text color="gray.400" fontSize="xs" fontWeight="normal" noOfLines={1}>{recipients?.subject}</Text>
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            {recipients?.loading ? <Spinner color={POKER_COLORS.cyan} /> :
              recipients?.error ? <Text color="red.300" fontSize="sm">{recipients.error}</Text> : (
              <>
                <HStack spacing={2} mb={3} flexWrap="wrap">
                  {(['all', 'sent', 'failed', 'bounced'] as const).map(f => {
                    const n = f === 'all' ? (recipients?.list?.length || 0) : (recipients?.counts?.[f] || 0);
                    return (
                      <Button key={f} size="xs" variant={recipientFilter === f ? 'solid' : 'outline'}
                              colorScheme={f === 'failed' ? 'red' : f === 'bounced' ? 'orange' : 'cyan'}
                              onClick={() => setRecipientFilter(f)}>
                        {f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1)} ({n})
                      </Button>
                    );
                  })}
                </HStack>
                {(recipients?.list || []).length === 0 ? (
                  <Text color="gray.400" fontSize="sm">
                    No recipients recorded for this subject yet. Campaigns sent before recipient
                    tracking existed show here once their past sends are recorded.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={1}>
                    {(recipients?.list || [])
                      .filter(r => recipientFilter === 'all' || r.status === recipientFilter)
                      .map(r => (
                        <HStack key={`${r.email}-${r.status}`} justify="space-between" bg="gray.800"
                                px={3} py={1.5} borderRadius="md">
                          <Text color="white" fontSize="xs" noOfLines={1}>{r.email}</Text>
                          <Badge colorScheme={r.status === 'sent' ? 'green' : r.status === 'bounced' ? 'orange' : 'red'}
                                 title={r.error || undefined}>
                            {r.status}
                          </Badge>
                        </HStack>
                      ))}
                  </VStack>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Preview */}
      <Modal isOpen={preview.isOpen} onClose={preview.onClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="#050f18" borderColor="#1B4055" borderWidth="1px">
          <ModalHeader color={POKER_COLORS.cyan} fontSize="md">Preview</ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            <Text color="gray.500" fontSize="xs" mb={3}>
              Inbox: <Text as="span" color="gray.200">{draft.subject}</Text>
              {draft.preheader && <Text as="span" color="gray.600"> - {draft.preheader}</Text>}
            </Text>
            <Box bg="#0A1F2E" borderWidth="1px" borderColor="#1B4055" borderRadius="14px" overflow="hidden">
              <HStack px="30px" pt="22px" pb="18px" borderBottomWidth="1px" borderColor="#1B4055" spacing="14px">
                <img src="https://pokersweepstars.com/logo.webp" alt="" width={56} height={56}
                     style={{ borderRadius: 12, display: 'block' }} />
                <Box>
                  <Text fontFamily="Georgia, serif" fontSize="21px" color="#FFD700">PokerSweepStars</Text>
                  <Text fontSize="10px" letterSpacing=".22em" textTransform="uppercase"
                        color="#00D9D9" mt="5px">Social Poker Platform</Text>
                </Box>
              </HStack>
              <Box px="30px" pt="28px" pb="30px">
                <Text fontFamily="Georgia, serif" fontSize="25px" lineHeight="1.22"
                      color="#E8F4F8" mb="16px">{draft.heading}</Text>
                {previewBody.map((p, i) => (
                  <Text key={i} fontSize="15px" lineHeight="1.62" color="#c9d9e2" mb="16px"
                        whiteSpace="pre-wrap">{p}</Text>
                ))}
                {draft.ctaLabel && draft.ctaUrl && (
                  <Box display="inline-block" bg="#FFD700" color="#241c00" borderRadius="26px"
                       px="34px" py="14px" fontSize="14px" fontWeight="bold" mt="10px">
                    {draft.ctaLabel}
                  </Box>
                )}
              </Box>
              <Box px="30px" pb="26px">
                <Text fontSize="11px" letterSpacing=".18em" textTransform="uppercase" color="#7FA0B0" mb="10px">
                  Get the app
                </Text>
                <HStack spacing="10px">
                  {[['Download on the', 'App Store'], ['Get it on', 'Google Play']].map(([s, b]) => (
                    <Box key={b} bg="#000" borderWidth="1px" borderColor="#A6A6A6" borderRadius="9px" px="16px" py="7px">
                      <Text fontSize="9px" color="#cbd5e1" lineHeight="1.2">{s}</Text>
                      <Text fontSize="15px" fontWeight="bold" color="#fff" lineHeight="1.25">{b}</Text>
                    </Box>
                  ))}
                </HStack>
              </Box>
              <Box px="30px" pt="18px" pb="24px" borderTopWidth="1px" borderColor="#1B4055">
                <Text fontSize="11px" color="#5a7686" mb="8px">
                  18+ only. Social gaming platform. Please play responsibly.
                </Text>
                <Text fontSize="11px" color="#7FA0B0">Unsubscribe from these emails · Terms &amp; Privacy</Text>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Send-to-all confirmation */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkTeal} borderColor="orange.500" borderWidth="2px">
          <ModalHeader color="orange.300">
            <HStack><AlertTriangle size={18} /><Text>Send this campaign?</Text></HStack>
          </ModalHeader>
          <ModalBody>
            <Text color="gray.300" fontSize="sm" mb={3}>
              Going to: <Text as="span" color="white" fontWeight="bold">
                {AUDIENCES.find(a => a.key === audience)?.label || audience}
                {audienceCounts ? ` (${audienceCounts[audience] ?? 0} recipients)` : ''}
              </Text>. There is no recall once it starts.
            </Text>
            {remaining && remaining.subject === draft.subject.trim() && remaining.already > 0 && (
              <Alert status="info" mb={3} borderRadius="md" bg="blue.900">
                <AlertIcon />
                <Text fontSize="xs" color="white">
                  {remaining.already} already received this subject and will be skipped.
                  {remaining.count !== null && ` This send goes to the ${remaining.count} who have not.`}
                </Text>
              </Alert>
            )}
            <Box bg="gray.800" p={3} borderRadius="md">
              <Text color="gray.500" fontSize="xs">Subject</Text>
              <Text color="white" fontSize="sm">{draft.subject}</Text>
            </Box>
            {!status?.verified && (
              <Alert status="warning" mt={3} borderRadius="md" bg="orange.900">
                <AlertIcon />
                <Text fontSize="xs" color="white">SMTP is not verified - the send may fail silently.</Text>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} color="gray.400">Cancel</Button>
            <Button colorScheme="yellow" onClick={() => send(false)} isLoading={sending === 'all'}>
              Send it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AdminEmailBroadcast;
